import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ArrowRight, AlertTriangle, ListChecks, CheckCircle2, Clock, Activity, DollarSign, Users, Bell, Plus,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/settings";
import { formatMoney } from "@/lib/format";
import { LargeTitle, Group, Row, KpiTile, IOS_ACCENT } from "@/components/mobile/ios";
import PullToRefresh from "@/components/mobile/PullToRefresh";

const STATUS_META = {
  todo: { label: "To do", className: "status-todo" },
  in_progress: { label: "In progress", className: "status-in_progress" },
  review: { label: "Review", className: "status-review" },
  done: { label: "Done", className: "status-done" },
  blocked: { label: "Blocked", className: "status-blocked" },
};

export default function Dashboard() {
  const { user, permissions } = useAuth();
  const { settings } = useSettings();
  const [data, setData] = useState(null);
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const canFinance = isAdmin || permissions?.can_view_finance || permissions?.can_track_finance;

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const totals = data?.totals || {};
  const recent = data?.recent_tasks || [];

  const loadData = async () => {
    try {
      const r = await api.get("/dashboard");
      setData(r.data);
    } catch (e) {}
  };

  // === MOBILE iOS-style render ===
  const todayStr = new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
  const openTasks = (totals.tasks || 0) - (totals.by_status?.done || 0);

  return (
    <Layout
      title={`Hello, ${user?.name?.split(" ")[0] || "there"}.`}
      subtitle={isAdmin ? "Control Room" : "Your Day"}
      actions={
        <Link to="/tasks" data-testid="dashboard-new-task-link">
          <Button className="h-10 px-4 rounded-xl btn-warm text-[13px] font-semibold">
            <Plus className="h-4 w-4 mr-1.5" /> New task
          </Button>
        </Link>
      }
    >
      {/* ============ MOBILE iOS view ============ */}
      <div className="lg:hidden">
        <PullToRefresh onRefresh={loadData}>
          <LargeTitle
            eyebrow={todayStr}
            title={`Hello, ${user?.name?.split(" ")[0] || "there"}.`}
          />

          <div className="px-4 grid grid-cols-2 gap-3">
            <KpiTile
              testId="kpi-open-tasks"
              icon={<ListChecks size={18} strokeWidth={2.4} />}
              label="Open tasks" value={openTasks}
              delta={`${totals.tasks || 0} total`}
              tint="#FFF4E6" iconColor={IOS_ACCENT}
            />
            <KpiTile
              testId="kpi-in-progress"
              icon={<Activity size={18} strokeWidth={2.4} />}
              label="In progress" value={totals.by_status?.in_progress || 0}
              delta="active now"
              tint="#E6F4FF" iconColor="#1F6FEB"
            />
            <KpiTile
              testId="kpi-overdue"
              icon={<AlertTriangle size={18} strokeWidth={2.4} />}
              label="Overdue" value={totals.overdue || 0}
              alert={(totals.overdue || 0) > 0}
              delta={(totals.overdue || 0) > 0 ? "needs review" : "all on track"}
              tint="#FFE9EC" iconColor="#D93B4B"
            />
            <KpiTile
              testId="kpi-completed"
              icon={<CheckCircle2 size={18} strokeWidth={2.4} />}
              label="Completed" value={totals.by_status?.done || 0}
              delta="this period"
              tint="#E7F8EF" iconColor="#1BA968"
            />
          </div>

          {recent.length > 0 && (
            <Group title="Recent activity">
              {recent.slice(0, 5).map((t, i) => (
                <Link key={t.task_id} to={`/tasks?open=${t.task_id}`}>
                  <Row
                    icon={<ListChecks size={16} />}
                    tint="#FFF4E6"
                    label={t.title}
                    sub={`${t.assignee_name || "Unassigned"} · ${new Date(t.updated_at).toLocaleDateString()}`}
                    last={i === Math.min(recent.length, 5) - 1}
                  />
                </Link>
              ))}
            </Group>
          )}

          {canFinance && (
            <Group title="Financials">
              <div className="px-5 py-4 border-b border-black/[0.05] flex items-end justify-between">
                <div>
                  <div className="text-[13px] text-[#8E8E93] font-medium">Total spend</div>
                  <div className="text-[28px] font-bold tracking-tight mt-0.5" data-testid="kpi-finance-total">
                    {formatMoney(totals.expense_total || 0, settings?.currency)}
                  </div>
                  <div className="text-[12px] text-[#8E8E93] mt-0.5">{totals.expense_count || 0} records</div>
                </div>
              </div>
              <Link to="/expenses">
                <Row label="Open ledger" data-testid="dashboard-go-expenses" last />
              </Link>
            </Group>
          )}

          {isAdmin && (
            <Group title="Team">
              <Link to="/users">
                <Row icon={<Users size={16} />} tint="#F2F2F7" label="Members" value={String(totals.user_count || 0)} last data-testid="dashboard-go-team" />
              </Link>
            </Group>
          )}

          <div className="h-32" />
        </PullToRefresh>
      </div>

      {/* ============ DESKTOP view (iOS-style) ============ */}
      <div className="hidden lg:block">
      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <DesktopKpi
          tint="#FFF4E6" iconColor="#E07A1F"
          icon={<ListChecks size={20} strokeWidth={2.2} />}
          label="Open tasks" value={openTasks}
          delta={`${totals.tasks || 0} total`}
          testId="kpi-open-tasks-d"
        />
        <DesktopKpi
          tint="#E6F4FF" iconColor="#1F6FEB"
          icon={<Activity size={20} strokeWidth={2.2} />}
          label="In progress" value={totals.by_status?.in_progress || 0}
          delta="active now"
          testId="kpi-in-progress-d"
        />
        <DesktopKpi
          tint="#FFE9EC" iconColor="#D93B4B"
          icon={<AlertTriangle size={20} strokeWidth={2.2} />}
          label="Overdue" value={totals.overdue || 0}
          alert={(totals.overdue || 0) > 0}
          delta={(totals.overdue || 0) > 0 ? "needs review" : "all on track"}
          testId="kpi-overdue-d"
        />
        <DesktopKpi
          tint="#E7F8EF" iconColor="#1BA968"
          icon={<CheckCircle2 size={20} strokeWidth={2.2} />}
          label="Completed" value={totals.by_status?.done || 0}
          delta="this period"
          testId="kpi-completed-d"
        />
      </div>

      {/* Two columns */}
      <div className="grid grid-cols-3 gap-4">
        {/* Recent activity */}
        <div className="col-span-2 soft-card p-7">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="eyebrow">Recent activity</div>
              <h3 className="text-[22px] font-bold tracking-tight text-[#1C1C1E] mt-1">Tasks updated lately</h3>
            </div>
            <Link to="/tasks" className="text-[13px] font-semibold text-[#1C1C1E] hover:underline underline-offset-4" data-testid="dashboard-see-all-tasks">
              See all →
            </Link>
          </div>
          {recent.length === 0 ? (
            <EmptyState text="No tasks yet — create your first one." cta={<Link to="/tasks"><Button className="h-10 px-4 rounded-xl btn-warm text-[13px] font-semibold" data-testid="dashboard-empty-create">Create task</Button></Link>} />
          ) : (
            <div className="space-y-1">
              {recent.map((t) => {
                const meta = STATUS_META_NEW[t.status] || STATUS_META_NEW.todo;
                return (
                  <Link
                    key={t.task_id}
                    to={`/tasks?open=${t.task_id}`}
                    data-testid={`dashboard-task-${t.task_id}`}
                    className="flex items-center gap-4 py-3 px-2 rounded-xl hover:bg-black/[0.02] cursor-pointer transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[15px] font-semibold text-[#1C1C1E] truncate">{t.title}</div>
                      <div className="text-[12px] text-[#8E8E93] mt-0.5">
                        {t.assignee_name || "Unassigned"} · Updated {new Date(t.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                    <span className="text-[11px] font-semibold px-2.5 py-1 rounded-md" style={{ background: meta.bg, color: meta.fg }}>
                      {meta.label}
                    </span>
                    <ArrowRight size={14} className="text-[#C7C7CC]" />
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <StatusBreakdown by={totals.by_status || {}} />
          {canFinance && (
            <div className="soft-card p-6">
              <div className="eyebrow">Financials</div>
              <div className="text-[32px] font-bold tracking-tight mt-2 text-[#1C1C1E]" data-testid="kpi-finance-total-d">
                {formatMoney(totals.expense_total || 0, settings?.currency)}
              </div>
              <div className="text-[12px] text-[#8E8E93] mt-0.5">
                {totals.expense_count || 0} records logged
              </div>
              <Link to="/expenses" data-testid="dashboard-go-expenses" className="mt-4 inline-flex items-center text-[13px] font-semibold" style={{ color: "#E07A1F" }}>
                Open ledger <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Link>
            </div>
          )}
          {isAdmin && (
            <div className="soft-card p-6">
              <div className="eyebrow">Team</div>
              <div className="text-[32px] font-bold tracking-tight mt-2 text-[#1C1C1E]">{totals.user_count || 0}</div>
              <div className="text-[12px] text-[#8E8E93]">active members</div>
              <Link to="/users" data-testid="dashboard-go-team" className="mt-4 inline-flex items-center text-[13px] font-semibold" style={{ color: "#E07A1F" }}>
                Manage team <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Link>
            </div>
          )}
        </div>
      </div>
      </div>{/* end desktop view */}
    </Layout>
  );
}

const STATUS_META_NEW = {
  todo: { label: "To do", bg: "#F2F2F7", fg: "#52525B" },
  in_progress: { label: "In progress", bg: "#E6F4FF", fg: "#1F6FEB" },
  review: { label: "Review", bg: "#FFF4E6", fg: "#A56014" },
  done: { label: "Done", bg: "#E7F8EF", fg: "#1BA968" },
  blocked: { label: "Blocked", bg: "#FFE9EC", fg: "#D93B4B" },
};

function DesktopKpi({ tint, iconColor, icon, label, value, delta, alert, testId }) {
  return (
    <div className="soft-card p-6" data-testid={testId}>
      <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: tint, color: iconColor }}>
        {icon}
      </div>
      <div className="text-[12px] uppercase tracking-[0.18em] font-semibold text-[#8E8E93] mt-5">{label}</div>
      <div className={`text-[42px] font-bold tracking-tight leading-none mt-2 ${alert ? "text-[#D93B4B]" : "text-[#1C1C1E]"}`}>
        {value}
      </div>
      <div className="text-[12px] text-[#8E8E93] mt-2">{delta}</div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent, testId, tint = "neutral" }) {
  return (
    <div className={`tech-card kpi-app rise lg:p-5 lg:rounded-md lg:border lg:border-neutral-200`} data-testid={testId}>
      <div className={`kpi-icon kpi-tint-${tint}`}>
        <Icon className="h-4 w-4" strokeWidth={1.8} />
      </div>
      <div className="overline pr-12">{label}</div>
      <div className={`mt-3 font-display text-3xl lg:text-4xl font-semibold tracking-tight ${accent === "danger" ? "text-red-700" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function StatusBreakdown({ by }) {
  const order = [
    { k: "todo", label: "Todo", color: "#E07A1F" },
    { k: "in_progress", label: "In progress", color: "#1F6FEB" },
    { k: "review", label: "Review", color: "#A56014" },
    { k: "done", label: "Done", color: "#1BA968" },
    { k: "blocked", label: "Blocked", color: "#D93B4B" },
  ];
  const total = order.reduce((s, o) => s + (by[o.k] || 0), 0) || 1;
  return (
    <div className="soft-card p-6">
      <div className="eyebrow mb-3">Status mix</div>
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-[#F2F2F7]">
        {order.map((o) => (
          <div key={o.k} style={{ width: `${((by[o.k] || 0) / total) * 100}%`, background: o.color }} />
        ))}
      </div>
      <div className="mt-4 space-y-2 text-[13px]">
        {order.map((o) => (
          <div key={o.k} className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-[#1C1C1E]">
              <span className="h-2 w-2 rounded-full" style={{ background: o.color }} />
              {o.label}
            </span>
            <span className="font-mono text-[12px] text-[#52525B]">{by[o.k] || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ text, cta }) {
  return (
    <div className="border border-dashed border-neutral-300 rounded-md p-8 text-center">
      <Clock className="h-5 w-5 mx-auto text-neutral-400" />
      <div className="mt-3 text-sm text-neutral-600">{text}</div>
      {cta && <div className="mt-4">{cta}</div>}
    </div>
  );
}
